/**
 * VersionDisplay component.
 *
 * Displays the current API version information.
 * Fetches version from /api/v1/version endpoint.
 */

import { useEffect, useState } from "react";

interface VersionData {
  version: string;
  build: string;
  environment: string;
}

interface VersionResponse {
  success: boolean;
  data: VersionData;
}

export function VersionDisplay() {
  const [version, setVersion] = useState<VersionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchVersion() {
      try {
        const response = await fetch("/api/v1/version");
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const result: VersionResponse = await response.json();
        // Unwrap the response - backend returns {success: true, data: {...}}
        setVersion(result.success ? result.data : null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    }

    fetchVersion();
  }, []);

  if (isLoading) {
    return <span className="text-muted">Loading...</span>;
  }

  if (error || !version) {
    return <span className="text-muted">Version unavailable</span>;
  }

  return (
    <span className="text-muted text-sm">
      v{version.version}
    </span>
  );
}
